'use client'

/**
 * The topic hooks: one `useMemo` each over `select-topics.ts`, plus the two write hooks.
 *
 * **The write hooks do not write.** `useFindOrCreateTopic` calls `document/operations/topics.ts`
 * through the bound `useDocumentOps()`, so the lookup and the create are one change transaction and
 * two calls in the same tick cannot both decide a topic is missing and mint it twice. ADR 0012
 * rule 2 permits a raw change handle only inside `src/document/internal/`, and nothing here holds one.
 */

import { useMemo } from 'react'
import { useDocumentOps, useDocumentStatus, useDocumentView } from '@/document'
import type { EntryType, Topic } from '@/document'

import {
  findTopicByName,
  selectTopics,
  selectTopicsWithUsage,
} from './select-topics'

export type { TopicWithUsage } from './select-topics'

/**
 * Every live topic, optionally scoped to one entry kind.
 *
 * @param entryType - omit for every kind; `null` selects the unscoped topics
 */
export function useTopics(entryType?: EntryType | null) {
  const doc = useDocumentView()
  const ready = useDocumentStatus()

  const topics = useMemo(() => selectTopics(doc, entryType), [doc, entryType])

  return { topics, isLoading: !ready }
}

/** Every live topic with the number of live entries carrying it, most-used first. */
export function useTopicsWithUsage() {
  const doc = useDocumentView()
  const ready = useDocumentStatus()

  const topics = useMemo(() => selectTopicsWithUsage(doc), [doc])

  return { topics, isLoading: !ready }
}

/** A lookup by exact name, bound to this device's document. */
export function useFindTopicByName() {
  const doc = useDocumentView()

  return (name: string, entryType?: EntryType | null): Topic | undefined =>
    findTopicByName(doc, name, entryType)
}

/** The id of the topic with this name, creating it if there is none. */
export function useFindOrCreateTopic() {
  const doc = useDocumentView()
  const ops = useDocumentOps()

  return (name: string, entryType: EntryType | null): string | null => {
    if (!doc) return null

    return ops.findOrCreateTopic(name, entryType)
  }
}

/** The same, for a list of names. Names that resolve to nothing are dropped. */
export function useFindOrCreateTopics() {
  const findOrCreateTopic = useFindOrCreateTopic()

  return (names: string[], entryType: EntryType | null): string[] =>
    names
      .map((name) => findOrCreateTopic(name, entryType))
      .filter((id): id is string => id !== null)
}
